import { Component, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EditorDataService } from '../editor-data/editor-data.service';
import { EditorEligibilityRuleHasEvent } from '../editor-eligibility-rule/editor-eligibility-rule-has-event.model';
import { formatDate } from '@angular/common';


@Component({
    selector: 'eas-editor-eligibility-rule-value-has-event',
    templateUrl: './editor-eligibility-rule-value.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorEligibilityRuleValueHasEventComponent implements OnChanges, OnInit, OnDestroy {
  @Input() rule: EditorEligibilityRuleHasEvent;
  @Input() affirmative: boolean;

  private destroyed$ = new Subject<void>();

  eventTypeGroupNames: { id: string, name: string }[];
  blueprintNamesWithCategoryNames: { id: string, name: string, categoryNames: { id: string, name: string }[] }[];

  value: string;

  constructor(@Inject(LOCALE_ID) private locale: string, private editorData: EditorDataService) {

  }

  ngOnInit() {
    this.editorData.eventTypeGroupNames$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        eventTypeGroupNames => {
          this.eventTypeGroupNames = eventTypeGroupNames;
          this.value = this.getValue();
        }
      );

    this.editorData.blueprintNamesWithCategoryNames$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        blueprintNamesWithCategoryNames => {
          this.blueprintNamesWithCategoryNames = blueprintNamesWithCategoryNames;
          this.value = this.getValue();
        }
      );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rule || changes.affirmative) {
      this.value = this.getValue();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getValue(): string {
    if (this.eventTypeGroupNames === undefined || this.blueprintNamesWithCategoryNames === undefined) {
      return $localize `Not available`;
    }

    const eventTypeGroups = this.rule.getEventTypesAsString(this.eventTypeGroupNames);
    let value = this.affirmative
      ? $localize `Candidate has one of the event types ${eventTypeGroups}`
      : $localize `Candidate has NONE of the event types ${eventTypeGroups}`;

    const blueprints = this.rule.getBlueprintsAsString(this.blueprintNamesWithCategoryNames);
    if (blueprints !== '') {
      value = this.affirmative
        ? $localize `${value} with one of the values ${blueprints}`
        : $localize `${value} with any of the values ${blueprints}`;
    }

    const formattedStart = this.rule.start === null ? null : formatDate(this.rule.start.toDate(), 'shortDate', this.locale);
    const formattedEnd = this.rule.end === null ? null : formatDate(this.rule.end.toDate(), 'shortDate', this.locale);
    if (this.rule.start !== null && this.rule.end !== null) {
      value = $localize `${value}, between ${formattedStart} and ${formattedEnd}`;
    } else if (this.rule.start !== null) {
      value = $localize `${value}, after ${formattedStart}`;
    } else if (this.rule.end !== null) {
      value = $localize `${value}, before ${formattedEnd}`;
    }

    if (this.rule.relativeStart !== null
      && (this.rule.relativeStart.years !== null || this.rule.relativeStart.months !== null || this.rule.relativeStart.days !== null)
    ) {
      value = $localize `${value}, in the last`;
      if ( this.rule.relativeStart.years !== null ) {
        value = $localize `${value} ${this.rule.relativeStart.years} years`;
      }
      if ( this.rule.relativeStart.months !== null ) {
        value = $localize `${value} ${this.rule.relativeStart.months} months`;
      }
      if ( this.rule.relativeStart.days !== null ) {
        value = $localize `${value} ${this.rule.relativeStart.days} days`;
      }
    }

    return value;
  }
}
